import React, { useContext, useEffect } from "react";
import {
  Box,
  Button,
  Grid,
  IconButton,
  LinearProgress,
  Typography,
} from "@mui/material";
import { FlowScreenWrapper } from "../shared/FlowScreenWrapper";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAuthContext } from "AuthContextWrapper"
import { OUTCOMES } from "../../../constants";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { RenderFileUploadBox } from "views/fileCabinet/CreateOrEditUserDocument";
import { documentsAPI } from "apis";
import { CheckCircle, HelpOutline } from "@mui/icons-material";
import { DOC_TYPE_ID_LETTERS_TESTAMENTARY, FileCabinetContext } from "FileCabinetContextWrapper";
import { Loading } from "views/shared/Loading";
import { EXECUTOR_VERIFICATION_STEP_LETTERS_OR_AGREEMENT, EXECUTOR_VERIFICATION_SUBMITTED_ID, findMostRecentDocAndSrc } from ".";
import { ExecutorVerificationContext } from "ExecutorVerificationContextWrapper";
import AtticusDrawer from "views/shared/drawer/AtticusDrawer";

export const UploadLettersOrAgreementScreen = (): JSX.Element => {
  const navigate = useNavigate();
  const { flow } = useAuthContext()
  const { docs, docSrcs, loadingDocs, loadingSrcs, setDocs, setDocSrcs } = useContext(FileCabinetContext);
  const { verificationStepId, getStepStatusId, setStepStatusId, setVerificationStepId } = useContext(ExecutorVerificationContext);
  const { screenName } = useParams();
  const currentScreen = flow?.getCurrentFlowScreen(screenName);
  const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);
  const [uploadedFiles, setUploadedFiles] = React.useState<FileList | undefined>();
  const [errors, setErrors] = React.useState<string[]>([]);
  const [progress, setProgress] = React.useState<number | undefined>();
  const [previewImage, setPreviewImage] = React.useState<JSX.Element>(<img src="/static/images/Letters_Sample.svg" style={{maxWidth:"100%",minHeight:"220px", maxHeight:"300px"}} />);
  const [previewImageKey, setPreviewImageKey] = React.useState<string>("initialKey");
  const [drawerOpen, setDrawerOpen] = React.useState<boolean>(false);
  
  useEffect(() => {
    setVerificationStepId(EXECUTOR_VERIFICATION_STEP_LETTERS_OR_AGREEMENT)
  }, [])

  const handleFileUpload = async (uploadedFiles: FileList): Promise<void> => {
    setUploadedFiles(uploadedFiles);
    setIsSubmitting(true);
    setProgress(undefined);
    const uploadFile = uploadedFiles?.item(0) || undefined;

    const result = await documentsAPI.saveDocument(
      "Letters Testamentary (or similar)",
      undefined,
      DOC_TYPE_ID_LETTERS_TESTAMENTARY,
      uploadFile,
      "",
      undefined,
      (percent) => setProgress(percent), // callback to display progress,
      true
    );
    if (!result) {
      setErrors(["Sorry, file upload failed.  Please try again."]);
    }

    if(uploadFile && result && result?.version?.thumbnailS3Key) {
      setDocs([...docs, result])
      setDocSrcs([...docSrcs,URL.createObjectURL(uploadFile)])
    }

    setIsSubmitting(false);
  };

  const handleComplete = async (): Promise<boolean> => {
    if (!currentScreen) throw Error("couldn't determine currentScreen"); 

    setIsSubmitting(true)

    try {
      if(getStepStatusId(verificationStepId) !== EXECUTOR_VERIFICATION_SUBMITTED_ID) {
        await setStepStatusId(verificationStepId, EXECUTOR_VERIFICATION_SUBMITTED_ID)
      }
    }catch(error) {
      console.error("issue updating step, " + (error as any)?.message)
      setIsSubmitting(false)
      return false
    }
    
    setIsSubmitting(false)

    flow?.navigateToNext(currentScreen, OUTCOMES.complete, navigate);
    return true;
  };

  const handleBack = async (): Promise<boolean> => {
    if (!currentScreen) throw Error("couldn't determine currentScreen");

    flow?.navigateToNext(currentScreen, OUTCOMES.back, navigate);
    return true;
  };

  
  useEffect(() => {
    // Preview image should be most recent doc, if any
    if(!loadingDocs && !loadingSrcs && docs && docs.length > 0 && docSrcs && docSrcs.length > 0) {
      const ret = findMostRecentDocAndSrc(docs, docSrcs, DOC_TYPE_ID_LETTERS_TESTAMENTARY)
      if(!ret) {
        return
      }

      setPreviewImage(<img src={ret[1]} style={{maxWidth:"100%",minHeight:"220px", maxHeight:"300px"}}  />)
      setPreviewImageKey(ret[1])
    }
  }, [loadingDocs, loadingSrcs, docs?.length, docSrcs?.length])

  if (!currentScreen) {
    return <></>;
  }

  return (
    <FlowScreenWrapper
      title="We’re almost there! Now all we need is a copy of your “Letters” or Executor Agreement received from the Probate Court"
      subTitle={
        "These are often referred to as “Letters,” or the documents someone applies for to become the legal administrator of an estate."
      }
    >
      <div>
        <Typography variant="body1">
          <b>Accepted documents</b> 
          <HelpOutline onClick={() => setDrawerOpen(true)} color="secondary" style={{ marginBottom:"-3px", cursor:"hand",marginLeft:"5px",width:"18px",height:"18px"}} />
        </Typography>
        <Typography variant="body1" component="ul" style={{lineHeight:"2rem"}}>
          <li>Letters of Administration</li>
          <li>Letters Testamentary</li>
          <li>Affidavit</li>
        </Typography>


        {(loadingDocs || loadingSrcs) && <Loading />}
        {(!loadingDocs && !loadingSrcs) && <RenderFileUploadBox
          uploadedFiles={uploadedFiles}
          setUploadedFiles={handleFileUpload}
          setErrors={setErrors}
          previewImage={previewImage}
          key={previewImageKey}
        />}

        {(!errors || errors.length === 0) && progress && progress < 100 && (
          <Box sx={{ width: "100%" }} key={"newfileprogressbox"}>
            <LinearProgress
              variant="determinate"
              value={progress}
              color="secondary"
              sx={{
                height: 16,
                mt: 2,
                mb: 1,
              }}
            />
          </Box>
        )}
        {(!errors || errors.length === 0) && progress === 100 && (
          <Grid
            container
            alignContent={"center"}
            alignItems={"center"}
            justifyContent={"center"}
            style={{ width: "100%" }}
          >
            <Grid
              item
              alignContent={"center"}
              alignItems={"center"}
              justifyContent={"center"}
            >
              <Typography
                variant={"body1"}
                component={"p"}
                color="secondary"
                style={{ marginRight: "10px", fontSize: "14pt" }}
              >
                Upload successful
              </Typography>
            </Grid>
            <Grid item>
              <CheckCircle fontSize="large" color="secondary" />
            </Grid>
          </Grid>
        )}
        {errors && errors.length > 0 && (
          <Typography variant={"body1"} color={"error"}>
            {errors.map((e) => (
              <>
                {e}
                <br />
              </>
            ))}
          </Typography>
        )}
        <Box
          sx={{
            display: "flex",
            flexDirection: "row-reverse",
            justifyContent: "space-between",
          }}
        >
          <Button
            color={"secondary"}
            variant="contained"
            disabled={isSubmitting}
            sx={{ minWidth: 180 }}
            onClick={async () => {
              setIsSubmitting(true);
              handleComplete();
              setIsSubmitting(false);
            }}
          >
            Continue
          </Button>
          <IconButton
            color={"secondary"}
            disabled={isSubmitting}
            aria-label="back"
            onClick={handleBack}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon color={"secondary"} />
          </IconButton>


          <AtticusDrawer
            variant="temporary"
            anchor="right"
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            contents={<div>
              <Typography variant={"h2"} style={{marginBottom:"1rem"}}>
                About receiving “Letters” and establishing executor capacity
              </Typography>
              <Typography variant="body1" style={{marginBottom:"1rem"}}>
              The collection of forms establishing authority for an executor over an estate is commonly 
              referred to as the executor’s “letters” of administration or testamentary. However, the exact 
              naming of the process or acceptable form may vary based upon locale practices, processes or type 
              of appointment, such as whether one is appointed via:
              </Typography>
              <Typography variant="body1" component="ul" style={{marginBottom:"1rem"}}>
                <li>Appointment through Last Will & Testament</li>
                <li>Qualification through small estate affidavit</li>
                <li>Appointed to server through probate court</li>
              </Typography>
              <Typography variant="body1" style={{marginBottom:"1rem"}}>
              While there are some hard and fast guidelines that outline which type of probate is most 
              appropriate for your situation, obtaining approval from the probate court is generally the final step, 
              as the probate clerk may sometimes allows for local processes, exceptions or particular allowances.
              </Typography>
              <Typography variant={"h2"} style={{marginBottom:"1rem"}}>
                What if I haven’t received or petitioned for Letters yet?
              </Typography>
              <Typography variant="body1" style={{marginBottom:"1rem"}}>
                If you are still waiting to receive a final signed copy of approved Letters back from the Court, that is fine.
              </Typography>
              <Typography variant="body1" style={{marginBottom:"1rem"}}>
                We will save your progress until you receive your signed documents back from the probate court 
                and they can be uploaded here to finish the Verification process.
              </Typography>
              <Typography variant={"h2"} style={{marginBottom:"1rem"}}>
                What if I don’t know what type of Probate I need?
              </Typography>
              <Typography variant="body1" style={{marginBottom:"1rem"}}>
                Not sure where to begin? Let Atticus help guide you through a 
                <Typography color="secondary" component="span">
                <Link to="/app/forms-and-files/probate" style={{marginLeft:"5px", marginRight:"5px", color:"inherit"}}>probate determination quiz</Link> 
                </Typography>
                for your situation.
              </Typography>
              <Typography variant="body1" style={{marginBottom:"1rem"}}>
                Or, learn more about the general probate process through our
                <Typography component="a" color="secondary" href="https://www.weareatticus.com/probate/what-is-probate" target="_blank" style={{marginLeft:"5px", marginRight:"5px"}}>
                  Probate 101 Guide
                </Typography>
              </Typography>
              <Typography variant="body1" style={{marginBottom:"1rem"}}>
                While some courts make forms available through their Probate Department or Law 
                Library, you may also need to create your own, or have a 
                local Trust & Estate attorney 
                do it for you.
              </Typography>
            </div>}
          />
        </Box>
      </div>
    </FlowScreenWrapper>
  );
};
